import { useState, useRef, useCallback, useEffect } from 'react';
import { useToolStore, type Tool } from '../stores/toolStore';
import { useTokenStore } from '../stores/tokenStore';
import { useMobStore } from '../stores/mobStore';
import { useDiceStore } from '../stores/diceStore';
import { useChatStore } from '../stores/chatStore';
import { useSessionStore } from '../stores/sessionStore';
import { useIsGM } from '../hooks/useIsGM';
import { Icon, type IconName } from './Icon';
import { Dice3DIcon } from './Dice3DIcon';
import type { ConditionType, EffectName, SpellShape } from '@machina/core';
import { EFFECT_COLORS } from '@machina/core';
import { useEffectStore } from '../stores/effectStore';
import { useUndoStore } from '../stores/undoStore';

/** Conditions shown in the toolbar menu (skip exhaustion tiers) */
const TOOLBAR_CONDITIONS: Array<{ type: ConditionType; label: string; icon: IconName; color: string; desc: string }> = [
  { type: 'blinded', label: 'Blinded', icon: 'eye-off', color: '#9ca3af', desc: "Can't see. Auto-fail sight checks. Attacks have disadvantage." },
  { type: 'charmed', label: 'Charmed', icon: 'heart-pulse', color: '#f472b6', desc: "Can't attack the charmer. Charmer has advantage on social checks." },
  { type: 'deafened', label: 'Deafened', icon: 'alert', color: '#9ca3af', desc: "Can't hear. Auto-fail hearing checks." },
  { type: 'frightened', label: 'Frightened', icon: 'alert', color: '#a78bfa', desc: "Disadvantage on checks/attacks while source is in sight. Can't willingly move closer." },
  { type: 'grappled', label: 'Grappled', icon: 'lock', color: '#fb923c', desc: 'Speed becomes 0. Ends if grappler is incapacitated or forced apart.' },
  { type: 'incapacitated', label: 'Incapacitated', icon: 'star', color: '#fbbf24', desc: "Can't take actions or reactions." },
  { type: 'invisible', label: 'Invisible', icon: 'ghost', color: '#67e8f9', desc: "Can't be seen without magic. Advantage on attacks, attacks against have disadvantage." },
  { type: 'paralyzed', label: 'Paralyzed', icon: 'snowflake', color: '#60a5fa', desc: 'Incapacitated, auto-fail STR/DEX saves. Attacks have advantage; melee hits are crits.' },
  { type: 'petrified', label: 'Petrified', icon: 'crystal', color: '#a8a29e', desc: 'Turned to stone. Incapacitated, unaware. Resistance to all damage.' },
  { type: 'poisoned', label: 'Poisoned', icon: 'droplet', color: '#4ade80', desc: 'Disadvantage on attack rolls and ability checks.' },
  { type: 'prone', label: 'Prone', icon: 'chevron-down', color: '#fbbf24', desc: 'Disadvantage on attacks. Melee attacks against have advantage; ranged have disadvantage.' },
  { type: 'restrained', label: 'Restrained', icon: 'chain', color: '#fb923c', desc: 'Speed 0. Attacks have disadvantage. DEX saves have disadvantage. Attacks against have advantage.' },
  { type: 'stunned', label: 'Stunned', icon: 'star', color: '#fbbf24', desc: 'Incapacitated, auto-fail STR/DEX saves. Attacks against have advantage.' },
  { type: 'unconscious', label: 'Unconscious', icon: 'moon', color: '#818cf8', desc: 'Incapacitated, drops what held, falls prone. Auto-fail STR/DEX. Melee hits are crits.' },
  { type: 'concentrating', label: 'Concentrating', icon: 'crystal', color: '#d4af37', desc: 'Maintaining a spell. CON save on damage or lose it.' },
  { type: 'bloodied', label: 'Bloodied', icon: 'droplet', color: '#dc2626', desc: 'Below half hit points. Visibly wounded and desperate.' },
];

interface ToolbarProps {
  onToolSelect?: (tool: Tool) => void;
}

interface Position {
  x: number;
  y: number;
}

type DieType = 'd4' | 'd6' | 'd8' | 'd10' | 'd12' | 'd20';

const DICE_TYPES: Array<{ type: DieType; label: string; color: string }> = [
  { type: 'd4', label: 'D4', color: 'var(--color-dice-d4)' },
  { type: 'd6', label: 'D6', color: 'var(--color-dice-d6)' },
  { type: 'd8', label: 'D8', color: 'var(--color-dice-d8)' },
  { type: 'd10', label: 'D10', color: 'var(--color-dice-d10)' },
  { type: 'd12', label: 'D12', color: 'var(--color-dice-d12)' },
  { type: 'd20', label: 'D20', color: 'var(--color-dice-d20)' },
];

/**
 * Vertical toolbar with expandable dice panel
 */
export function VerticalToolbar({ onToolSelect }: ToolbarProps) {
  const activeTool = useToolStore((s) => s.activeTool);
  const setActiveTool = useToolStore((s) => s.setActiveTool);
  const selectedCount = useTokenStore((s) => s.selectedIds.size);
  const removeSelected = useTokenStore((s) => s.removeSelected);
  const roll = useDiceStore((s) => s.roll);
  const isReady = useDiceStore((s) => s.isReady);
  const { addDiceRoll } = useChatStore();
  const userName = useSessionStore((s) => s.userName);
  const isGM = useIsGM();

  const [diceExpanded, setDiceExpanded] = useState(false);
  const [diceCount, setDiceCount] = useState(1);
  const [modifier, setModifier] = useState<'normal' | 'advantage' | 'disadvantage'>('normal');
  const [statusExpanded, setStatusExpanded] = useState(false);
  const [spellExpanded, setSpellExpanded] = useState(false);
  const [spellEffect, setSpellEffect] = useState<EffectName>('fire');
  const [spellShape, setSpellShape] = useState<SpellShape>('circle');
  const [spellRadius, setSpellRadius] = useState(4); // in grid squares
  const activeEffectCount = useEffectStore((s) => s.activeEffects.size);
  const clearAllEffects = useEffectStore((s) => s.clearAll);

  // Undo pill — surfaces under the trash icon for 30s after a delete.
  const undoPillId = useUndoStore((s) => s.pillActionId);
  const undoPillDescription = useUndoStore((s) => s.pillDescription);
  const undoPill = useUndoStore((s) => s.undoPill);
  const dismissPill = useUndoStore((s) => s.dismissPill);

  // Draggable toolbar state
  const [position, setPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; elemX: number; elemY: number } | null>(null);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    // Only start drag from the handle area — don't intercept buttons or inputs (e.g. range slider)
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('input') || target.closest('select')) return;

    e.preventDefault();
    setIsDragging(true);

    const rect = dragRef.current?.getBoundingClientRect();
    const parentRect = dragRef.current?.parentElement?.getBoundingClientRect();
    if (rect && parentRect) {
      // Calculate position relative to parent, not viewport
      dragStartRef.current = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        elemX: rect.left - parentRect.left,
        elemY: rect.top - parentRect.top,
      };
    }
  }, []);

  const handleDrag = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return;

    const deltaX = e.clientX - dragStartRef.current.mouseX;
    const deltaY = e.clientY - dragStartRef.current.mouseY;

    setPosition({
      x: dragStartRef.current.elemX + deltaX,
      y: dragStartRef.current.elemY + deltaY,
    });
  }, [isDragging]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    dragStartRef.current = null;
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    window.addEventListener('mousemove', handleDrag);
    window.addEventListener('mouseup', handleDragEnd);
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, handleDrag, handleDragEnd]);

  const tools: Array<{ id: Tool; label: string; icon: IconName }> = [
    { id: 'select', label: 'Select', icon: 'select' },
    { id: 'ping', label: 'Ping', icon: 'crosshairs' },
    { id: 'spell', label: 'Spell Area', icon: 'wand' },
    { id: 'token', label: 'Token', icon: 'token' },
    { id: 'measure', label: 'Measure', icon: 'measure' },
    { id: 'draw', label: 'Draw', icon: 'draw' },
  ];

  const handleToolClick = (tool: Tool) => {
    // Clicking select when already on select = hard deselect all
    if (tool === 'select' && activeTool === 'select') {
      useTokenStore.getState().clearSelection();
    }
    setActiveTool(tool);
    onToolSelect?.(tool);
    setStatusExpanded(false);
    if (tool === 'dice') {
      setDiceExpanded(!diceExpanded);
      setSpellExpanded(false);
    } else if (tool === 'spell') {
      setSpellExpanded(!spellExpanded);
      setDiceExpanded(false);
    } else {
      setDiceExpanded(false);
      setSpellExpanded(false);
    }
  };

  // Sync spell config to window for MapCanvas click handler
  useEffect(() => {
    (window as unknown as { __spellConfig?: { effect: string; shape: string; radius: number } }).__spellConfig = {
      effect: spellEffect,
      shape: spellShape,
      radius: spellRadius,
    };
  }, [spellEffect, spellShape, spellRadius]);

  const handleDiceToggle = () => {
    setDiceExpanded(!diceExpanded);
    setStatusExpanded(false);
    setActiveTool('dice');
    onToolSelect?.('dice');
  };

  const handleDeleteSelected = () => {
    if (selectedCount > 0) {
      removeSelected();
    }
  };

  // --- Status menu helpers ---
  const selectedIds = useTokenStore((s) => s.selectedIds);
  const tokens = useTokenStore((s) => s.tokens);
  const mobs = useMobStore((s) => s.mobs);
  const applyCondition = useMobStore((s) => s.applyCondition);
  const removeCondition = useMobStore((s) => s.removeCondition);

  const selectedTokenId = selectedIds.size > 0 ? Array.from(selectedIds)[0] : null;
  const selectedToken = selectedTokenId ? tokens.get(selectedTokenId) : null;
  const linkedMob = selectedToken?.combatantId ? mobs.get(selectedToken.combatantId) : null;

  const activeConditions = new Set(
    linkedMob?.conditions.map((c) => c.type) ?? selectedToken?.conditions?.map((c) => c.type) ?? []
  );

  // Track recently toggled condition for flash animation
  const [flashCondition, setFlashCondition] = useState<string | null>(null);
  // Track hovered condition for description expand
  const [hoveredCondition, setHoveredCondition] = useState<string | null>(null);

  const handleToggleCondition = (conditionType: ConditionType) => {
    if (!selectedToken) return;

    // Flash animation
    setFlashCondition(conditionType);
    setTimeout(() => setFlashCondition(null), 600);

    if (linkedMob) {
      // Mob-linked token — use mobStore
      if (activeConditions.has(conditionType)) {
        removeCondition(linkedMob.id, conditionType);
      } else {
        applyCondition(linkedMob.id, conditionType, undefined, undefined);
      }
    } else {
      // Unlinked token — update token conditions directly
      const currentConditions = selectedToken.conditions ?? [];
      const newConditions = activeConditions.has(conditionType)
        ? currentConditions.filter((c) => c.type !== conditionType)
        : [...currentConditions, { type: conditionType, source: undefined, duration: null, saveEndOfTurn: false }];
      useTokenStore.getState().updateToken({ id: selectedToken.id, conditions: newConditions });
    }
  };

  // Determine flyout direction — flip to left when toolbar is near right edge
  const getFlyoutSide = (): 'right' | 'left' => {
    const parent = dragRef.current?.parentElement;
    if (!parent || !dragRef.current) return 'right';
    const parentRect = parent.getBoundingClientRect();
    const toolbarRect = dragRef.current.getBoundingClientRect();
    const spaceRight = parentRect.right - toolbarRect.right;
    // Flyout needs ~300px; flip if not enough room
    return spaceRight < 310 ? 'left' : 'right';
  };

  const handleRollDice = async (dieType: DieType) => {
    if (!isReady) return;

    const baseNotation = `${diceCount}${dieType}`;

    // For d20 advantage/disadvantage, roll 2d20 in one throw
    if (modifier !== 'normal' && dieType === 'd20') {
      const result = await roll('2d20');

      if (result && result.rolls.length >= 2) {
        const val1 = result.rolls[0].value;
        const val2 = result.rolls[1].value;
        const chosen = modifier === 'advantage'
          ? Math.max(val1, val2)
          : Math.min(val1, val2);

        const combinedResult = {
          notation: `1d20 (${modifier})`,
          rolls: result.rolls,
          total: chosen,
          advDisRolls: [val1, val2] as [number, number],
          discardedValue: modifier === 'advantage' ? Math.min(val1, val2) : Math.max(val1, val2),
        };

        addDiceRoll({
          sender: userName || 'Player',
          notation: `1d20`,
          result: combinedResult as any,
          rollType: modifier,
        });
      }
    } else {
      // Normal roll or non-d20 adv/dis
      const result = await roll(baseNotation);
      if (result) {
        addDiceRoll({
          sender: userName || 'Player',
          notation: baseNotation,
          result,
          rollType: 'normal',
        });
      }
    }
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    width: '44px',
    height: '44px',
    borderRadius: 'var(--radius-md)',
    backgroundColor: isActive
      ? 'var(--color-primary)'
      : 'var(--color-background-elevated)',
    color: isActive
      ? 'var(--color-text)'
      : 'var(--color-text-secondary)',
    border: `var(--border-width-thin) solid ${
      isActive
        ? 'var(--color-accent)'
        : 'var(--color-border)'
    }`,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all var(--transition-fast)',
  });

  return (
    <div
      ref={dragRef}
      data-tour="toolbar"
      onMouseDown={handleDragStart}
      style={{
        position: 'absolute',
        ...(position
          ? { left: position.x, top: position.y, transform: 'none' }
          : { left: 'var(--spacing-md)', top: '50%', transform: 'translateY(-50%)' }
        ),
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-xs)',
        backgroundColor: 'var(--color-surface)',
        padding: 'var(--spacing-sm)',
        borderRadius: 'var(--radius-lg)',
        border: `var(--border-width-normal) solid var(--color-border)`,
        boxShadow: 'var(--shadow-lg)',
        zIndex: 50,
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: 'none',
      }}>
      {/* Drag handle */}
      <div
        style={{
          height: '6px',
          marginBottom: 'var(--spacing-xs)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'grab',
        }}
        title="Drag to move"
      >
        <div style={{
          width: '24px',
          height: '3px',
          backgroundColor: 'var(--color-border)',
          borderRadius: 'var(--radius-full)',
        }} />
      </div>

      {/* Map tools */}
      {tools.map((tool) => (
        <button
          key={tool.id}
          onClick={() => handleToolClick(tool.id)}
          title={tool.label}
          style={buttonStyle(activeTool === tool.id)}
          onMouseEnter={(e) => {
            if (activeTool !== tool.id) {
              e.currentTarget.style.borderColor = 'var(--color-border-focus)';
              e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)';
            }
          }}
          onMouseLeave={(e) => {
            if (activeTool !== tool.id) {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.backgroundColor = 'var(--color-background-elevated)';
            }
          }}
        >
          <Icon name={tool.icon} size={20} />
        </button>
      ))}

      {/* Separator */}
      <div style={{
        height: '1px',
        backgroundColor: 'var(--color-border)',
        margin: 'var(--spacing-xs) 0',
      }} />

      {/* Dice button with expand */}
      <button
        onClick={handleDiceToggle}
        title="Roll Dice"
        style={{
          ...buttonStyle(activeTool === 'dice'),
          position: 'relative',
        }}
      >
        <Icon name="dice" size={20} />
        <Icon
          name={diceExpanded ? 'chevron-down' : 'chevron-right'}
          size={12}
          style={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            opacity: 0.6,
          }}
        />
      </button>

      {/* Expanded dice panel - styled like the dice tray */}
      {diceExpanded && (
        <div style={{
          position: 'absolute',
          ...(getFlyoutSide() === 'right'
            ? { left: '100%', marginLeft: 'var(--spacing-sm)' }
            : { right: '100%', marginRight: 'var(--spacing-sm)' }
          ),
          top: 0,
          background: 'linear-gradient(145deg, #3d2e24 0%, #2a1f18 50%, #1a1410 100%)',
          padding: '3px',
          borderRadius: '12px',
          boxShadow: `
            0 0 0 1px #8b6914,
            0 0 0 2px #5c4a1f,
            0 8px 32px rgba(0, 0, 0, 0.6)
          `,
          minWidth: '280px',
        }}>
          {/* Inner container */}
          <div style={{
            background: 'linear-gradient(180deg, #2d241c 0%, #1e1814 100%)',
            borderRadius: '10px',
            padding: '14px',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(139, 105, 20, 0.3)',
            }}>
              <span style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#d4af37',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              }}>
                Dice Tray
              </span>
              <Icon name="dice" size={14} color="#8b6914" />
            </div>

            {/* Dice count control */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '12px',
              padding: '8px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '6px',
            }}>
              <span style={{
                fontSize: '11px',
                color: '#a08050',
                width: '36px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}>
                Qty
              </span>
              <button
                onClick={() => setDiceCount(Math.max(1, diceCount - 1))}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '4px',
                  background: 'linear-gradient(145deg, #3d3d3d 0%, #1a1a1a 100%)',
                  border: '1px solid #555',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ccc',
                }}
              >
                <Icon name="minus" size={12} />
              </button>
              <span style={{
                width: '32px',
                textAlign: 'center',
                fontSize: '18px',
                fontWeight: 700,
                color: '#d4af37',
                fontFamily: 'monospace',
              }}>
                {diceCount}
              </span>
              <button
                onClick={() => setDiceCount(Math.min(10, diceCount + 1))}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '4px',
                  background: 'linear-gradient(145deg, #3d3d3d 0%, #1a1a1a 100%)',
                  border: '1px solid #555',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ccc',
                }}
              >
                <Icon name="plus" size={12} />
              </button>
            </div>

            {/* Modifier selector */}
            <div style={{
              display: 'flex',
              gap: '4px',
              marginBottom: '12px',
            }}>
              {(['normal', 'advantage', 'disadvantage'] as const).map((mod) => (
                <button
                  key={mod}
                  onClick={() => setModifier(mod)}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                    background: modifier === mod
                      ? mod === 'advantage'
                        ? 'linear-gradient(145deg, #34d399 0%, #059669 100%)'
                        : mod === 'disadvantage'
                          ? 'linear-gradient(145deg, #f87171 0%, #dc2626 100%)'
                          : 'linear-gradient(145deg, #d4af37 0%, #a08030 100%)'
                      : 'rgba(0,0,0,0.2)',
                    color: modifier === mod ? '#1a1a1a' : '#888',
                    border: modifier === mod
                      ? 'none'
                      : '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {mod === 'normal' ? 'Normal' : mod === 'advantage' ? 'Adv' : 'Dis'}
                </button>
              ))}
            </div>

            {/* Dice grid - felt background */}
            <div style={{
              background: 'radial-gradient(ellipse at center, #1e5c3a 0%, #1a472a 60%, #0f2d1a 100%)',
              borderRadius: '8px',
              padding: '14px',
              border: '2px solid #2d1f15',
              boxShadow: 'inset 0 0 20px rgba(0, 0, 0, 0.4)',
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 76px)',
                gap: '10px',
                justifyContent: 'center',
              }}>
                {DICE_TYPES.map((die) => (
                  <button
                    key={die.type}
                    onClick={() => handleRollDice(die.type)}
                    disabled={!isReady}
                    style={{
                      width: '76px',
                      height: '76px',
                      padding: '6px',
                      borderRadius: '8px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      cursor: isReady ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease',
                      opacity: isReady ? 1 : 0.5,
                    }}
                    onMouseEnter={(e) => {
                      if (isReady) {
                        e.currentTarget.style.background = 'rgba(212, 175, 55, 0.2)';
                        e.currentTarget.style.borderColor = '#d4af37';
                        e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(0,0,0,0.3)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                      e.currentTarget.style.transform = 'scale(1) translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <Dice3DIcon type={die.type} size={48} />
                    <span style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: '#a08050',
                      letterSpacing: '0.5px',
                    }}>
                      {die.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Status hint */}
            <div style={{
              marginTop: '10px',
              padding: '6px 8px',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
              fontSize: '10px',
              color: '#8b6914',
              textAlign: 'center',
              fontStyle: 'italic',
            }}>
              {!isReady
                ? 'Initializing dice...'
                : modifier !== 'normal'
                  ? `Rolling with ${modifier}`
                  : diceCount > 1
                    ? `Rolling ${diceCount} dice`
                    : 'Click a die to roll'}
            </div>
          </div>
        </div>
      )}

      {/* Spell area config flyout */}
      {spellExpanded && (
        <div style={{
          position: 'absolute',
          ...(getFlyoutSide() === 'right'
            ? { left: '100%', marginLeft: 'var(--spacing-sm)' }
            : { right: '100%', marginRight: 'var(--spacing-sm)' }
          ),
          bottom: 120,
          background: 'linear-gradient(145deg, #2d1f2d 0%, #1a1018 50%, #14101a 100%)',
          padding: '3px',
          borderRadius: '12px',
          boxShadow: '0 0 0 1px rgba(212, 175, 55, 0.3), 0 8px 32px rgba(0, 0, 0, 0.6)',
          zIndex: 100,
          width: 200,
        }}>
          <div style={{
            background: 'linear-gradient(180deg, #241c24 0%, #1a141e 100%)',
            borderRadius: '10px',
            padding: '10px',
          }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#d4af37', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, borderBottom: '1px solid rgba(139,105,20,0.3)', paddingBottom: 6 }}>
              Spell Area
            </div>

            {/* Effect type selector */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Effect</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {(['fire', 'ice', 'divine', 'decay', 'eldritch', 'psychic', 'thunder', 'fey', 'menace', 'darkness'] as EffectName[]).map(e => {
                  const colors = EFFECT_COLORS[e];
                  // Use glow color for very dark effects so the label is visible
                  const raw = colors.primary > 0x333333 ? colors.primary : colors.glow > 0x333333 ? colors.glow : 0x8866aa;
                  const hexColor = '#' + raw.toString(16).padStart(6, '0');
                  return (
                    <button key={e} onClick={() => setSpellEffect(e)} style={{
                      padding: '3px 6px', borderRadius: 4, border: 'none', fontSize: 10,
                      background: spellEffect === e ? `${hexColor}33` : 'transparent',
                      color: spellEffect === e ? hexColor : '#777',
                      fontWeight: spellEffect === e ? 600 : 400,
                      cursor: 'pointer', fontFamily: 'Georgia, serif', textTransform: 'capitalize',
                    }}>
                      {e}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Shape selector */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>Shape</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {(['circle', 'square', 'cone', 'line'] as SpellShape[]).map(s => (
                  <button key={s} onClick={() => setSpellShape(s)} style={{
                    flex: 1, padding: '4px', borderRadius: 4, border: 'none', fontSize: 10,
                    background: spellShape === s ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.05)',
                    color: spellShape === s ? '#d4af37' : '#777',
                    fontWeight: spellShape === s ? 600 : 400,
                    cursor: 'pointer', fontFamily: 'Georgia, serif', textTransform: 'capitalize',
                  }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Radius selector */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', marginBottom: 4 }}>
                Radius: {spellRadius * 5}ft ({spellRadius} squares)
              </div>
              <input
                type="range" min={1} max={12} value={spellRadius}
                onChange={(e) => setSpellRadius(Number(e.target.value))}
                style={{ width: '100%', accentColor: '#d4af37' }}
              />
            </div>

            {/* Clear all + info */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>
                Click map to place
              </span>
              {activeEffectCount > 0 && (
                <button onClick={clearAllEffects} style={{
                  padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(212,175,55,0.3)',
                  background: 'transparent', color: '#d4af37', fontSize: 9, cursor: 'pointer',
                  fontFamily: 'Georgia, serif',
                }}>
                  Clear All ({activeEffectCount})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Conditions — available to all players */}
      <div style={{
        height: '1px',
        backgroundColor: 'var(--color-border)',
        margin: 'var(--spacing-xs) 0',
      }} />

      {/* Status / Conditions button */}
      <button
            onClick={() => { setStatusExpanded(!statusExpanded); setDiceExpanded(false); }}
            disabled={selectedCount === 0}
            title={selectedCount > 0 ? 'Apply condition to selected token' : 'Select a token first'}
            style={{
              ...buttonStyle(statusExpanded),
              opacity: selectedCount > 0 ? 1 : 0.4,
              cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
              position: 'relative',
            }}
          >
            <Icon name="sparkle" size={20} />
            {activeConditions.size > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                backgroundColor: '#d4af37',
                color: '#000',
                borderRadius: 'var(--radius-full)',
                width: '16px',
                height: '16px',
                fontSize: '10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {activeConditions.size}
              </span>
            )}
          </button>

          {/* Status flyout panel — two-column grid, no scrollbar */}
          {statusExpanded && selectedCount > 0 && (
            <div style={{
              position: 'absolute',
              ...(getFlyoutSide() === 'right'
                ? { left: '100%', marginLeft: 'var(--spacing-sm)' }
                : { right: '100%', marginRight: 'var(--spacing-sm)' }
              ),
              bottom: 0,
              background: 'linear-gradient(145deg, #2d1f2d 0%, #1a1018 50%, #14101a 100%)',
              padding: '3px',
              borderRadius: '12px',
              boxShadow: `
                0 0 0 1px rgba(212, 175, 55, 0.3),
                0 0 0 2px rgba(92, 74, 31, 0.3),
                0 8px 32px rgba(0, 0, 0, 0.6)
              `,
              zIndex: 100,
            }}>
              <div style={{
                background: 'linear-gradient(180deg, #241c24 0%, #1a141e 100%)',
                borderRadius: '10px',
                padding: '10px',
              }}>
                {/* Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '8px',
                  paddingBottom: '6px',
                  borderBottom: '1px solid rgba(139, 105, 20, 0.3)',
                }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#d4af37',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  }}>
                    Conditions
                  </span>
                  <span style={{
                    fontSize: '10px',
                    color: 'rgba(212, 175, 55, 0.5)',
                    fontStyle: 'italic',
                  }}>
                    {selectedToken?.name || 'Token'}
                  </span>
                </div>

                {/* Two-column condition grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '2px',
                }}>
                  {TOOLBAR_CONDITIONS.map((cond) => {
                    const isActive = activeConditions.has(cond.type);
                    const isFlashing = flashCondition === cond.type;
                    const isHovered = hoveredCondition === cond.type;
                    return (
                      <div key={cond.type} style={{ position: 'relative' }}>
                        <button
                          onClick={() => handleToggleCondition(cond.type)}
                          onMouseEnter={() => setHoveredCondition(cond.type)}
                          onMouseLeave={() => setHoveredCondition(null)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '5px 7px',
                            borderRadius: '6px',
                            border: 'none',
                            width: '100%',
                            background: isFlashing
                              ? `${cond.color}44`
                              : isActive
                                ? `linear-gradient(90deg, ${cond.color}22 0%, ${cond.color}11 100%)`
                                : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            textAlign: 'left',
                            animation: isFlashing ? 'conditionFlash 0.6s ease-out' : undefined,
                          }}
                        >
                          <div style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isActive ? `${cond.color}33` : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${isActive ? cond.color : 'rgba(255,255,255,0.1)'}`,
                            flexShrink: 0,
                            transition: 'all 0.15s ease',
                            transform: isFlashing ? 'scale(1.3)' : 'scale(1)',
                          }}>
                            <Icon
                              name={cond.icon}
                              size={11}
                              color={isActive ? cond.color : '#666'}
                            />
                          </div>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive ? cond.color : '#999',
                            fontFamily: 'Georgia, serif',
                            whiteSpace: 'nowrap',
                          }}>
                            {cond.label}
                          </span>
                        </button>

                        {/* Hover tooltip — expands to show icon + description */}
                        {isHovered && (
                          <div style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginBottom: '6px',
                            background: 'linear-gradient(180deg, #2a1f2a 0%, #1a141e 100%)',
                            border: `1px solid ${cond.color}44`,
                            borderRadius: '8px',
                            padding: '8px 10px',
                            width: '220px',
                            boxShadow: `0 4px 16px rgba(0,0,0,0.5), 0 0 8px ${cond.color}22`,
                            zIndex: 200,
                            pointerEvents: 'none',
                            animation: 'tooltipFadeIn 0.15s ease-out',
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              marginBottom: '4px',
                            }}>
                              <Icon name={cond.icon} size={14} color={cond.color} />
                              <span style={{
                                fontSize: '12px',
                                fontWeight: 700,
                                color: cond.color,
                                fontFamily: 'Georgia, serif',
                              }}>
                                {cond.label}
                              </span>
                            </div>
                            <p style={{
                              margin: 0,
                              fontSize: '10px',
                              lineHeight: '1.4',
                              color: '#b0a090',
                              fontFamily: 'Georgia, serif',
                            }}>
                              {cond.desc}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <style>{`
                @keyframes conditionFlash {
                  0% { background-color: rgba(255, 255, 255, 0.3); }
                  100% { background-color: transparent; }
                }
                @keyframes tooltipFadeIn {
                  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
                  to { opacity: 1; transform: translateX(-50%) translateY(0); }
                }
              `}</style>
            </div>
          )}

      {/* Delete button — GM only */}
      {isGM && (
        <>
          <button
            onClick={handleDeleteSelected}
            disabled={selectedCount === 0}
            title={selectedCount > 0 ? `Delete ${selectedCount} token(s)` : 'Select tokens to delete'}
            style={{
              ...buttonStyle(false),
              backgroundColor: selectedCount > 0
                ? 'var(--color-error)'
                : 'var(--color-background-elevated)',
              borderColor: selectedCount > 0
                ? 'var(--color-error)'
                : 'var(--color-border)',
              opacity: selectedCount > 0 ? 1 : 0.4,
              cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
              position: 'relative',
            }}
          >
            <Icon name="trash" size={20} />
            {selectedCount > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                backgroundColor: 'var(--color-accent)',
                color: 'var(--color-text-inverse)',
                borderRadius: 'var(--radius-full)',
                width: '16px',
                height: '16px',
                fontSize: '10px',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {selectedCount}
              </span>
            )}
          </button>

          {/* Undo pill — appears for 30s after a delete, single slot. */}
          {undoPillId && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                marginTop: '4px',
                padding: '4px 6px',
                backgroundColor: 'var(--color-background-elevated)',
                border: '1px solid var(--color-accent, #d4af37)',
                borderRadius: 'var(--radius-sm, 4px)',
                fontSize: '10px',
                color: 'var(--color-text-primary)',
                animation: 'undoPillFadeIn 200ms ease-out',
                maxWidth: '120px',
              }}
              title={undoPillDescription ?? 'Undo last delete'}
            >
              <button
                onClick={() => undoPill()}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-accent, #d4af37)',
                  fontWeight: 600,
                  fontSize: '11px',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  letterSpacing: '0.04em',
                }}
              >
                ↶ Undo
              </button>
              <button
                onClick={() => dismissPill()}
                title="Dismiss"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  padding: '0 4px',
                  fontSize: '12px',
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
          )}
          <style>{`
            @keyframes undoPillFadeIn {
              from { opacity: 0; transform: translateY(-2px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </>
      )}
    </div>
  );
}
